import { useTableServer } from "@/hooks/useTableServer";
import { useCallback, useState } from "react";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import {
  IEnquiryEntry,
  LeadFeedbackStatus,
} from "@/utilities/types/EnquiryEntry";
import { Title } from "rizzui";
import toast from "react-hot-toast";
import CardLeadFeedback from "./CardLeadFeedback";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";

export default function LeadsFeedbackMobileTable() {
  const { profile } = useProfile();
  const {
    refresh,
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
  } = useTableServer({
    server: {
      request:
        profile?.role === Roles.AGENCY
          ? EnquiryEntriesRepository.getListFeedbackByAgency
          : EnquiryEntriesRepository.getListFeedbackByUser,
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeedback = useCallback(
    async (id: string, type: LeadFeedbackStatus) => {
      try {
        setIsSubmitting(true);
        await EnquiryEntriesRepository.updateFeedback(id, type);
        await refresh();
        toast.success("Feedback Submitted");
      } catch (error) {
        toast.error("Failed to submit feedback");
      } finally {
        setIsSubmitting(false);
      }
    },
    [refresh]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (tableData.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">No data found</p>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex flex-col items-start mb-5">
        <Title as="h5" className="text-xl md:text-2xl">
          Leads Feedback
        </Title>
        <p className="text-gray-500">
          To better optimise your ads, kindly share if you were able to contact
          the lead.
        </p>
      </div>

      <div className="space-y-4">
        {tableData.map((row: IEnquiryEntry) => (
          <CardLeadFeedback
            key={row.id}
            lead={row}
            onFeedback={handleFeedback}
            isSubmitting={isSubmitting}
          />
        ))}
      </div>

      {/* Simple Pagination */}
      {totalItems > pageSize && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            onClick={() => handlePaginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm bg-gray-100 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {Math.ceil(totalItems / pageSize)}
          </span>
          <button
            onClick={() => handlePaginate(currentPage + 1)}
            disabled={currentPage >= Math.ceil(totalItems / pageSize)}
            className="px-4 py-2 text-sm bg-gray-100 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
